import { first, isEmpty, isEqual, isNil } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { RelatedEntityField, RelatedItem } from '@shared/data/entity/fields/RelatedEntityField';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { useWorkflowEntitiesByIds, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  getPrefillSourceComponent,
  getRelatedCardDefaultLookup,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

/** One prefill's write to one field, kept only for as long as that write is still untouched. */
export interface PrefillWrite {
  /** The cards the prefill put there. A field can hold these alongside cards from elsewhere. */
  addedIds: Set<string>;
  /** Everything the field held straight after the write, so a later edit to it is recognisable. */
  resultIds: string[];
  /**
   * Whether the form has come back round holding `resultIds`.
   *
   * A write is recorded in the same pass that calls `updateField`, one render before the form
   * reflects it, so until this is set the field not matching `resultIds` means the write is still
   * in flight — not that anyone has edited it.
   */
  hasLanded: boolean;
}

/**
 * What each prefill has written, by component id — the form's record of what it filled in versus
 * what the person using it chose.
 *
 * Shared across every prefilled field on one form, because prefills chain: a Meter arriving as a
 * query param fills Asset, and Asset being filled is what lets Location fill from it. Each link
 * has to see that the one before it was filled by the form rather than picked by hand.
 *
 * An entry lives only while the field still holds exactly what the prefill left there. The moment
 * the person edits that field the entry is dropped, and it is never restored — so putting the same
 * card back by hand does not make it the form's choice again.
 */
export type PrefillWrites = Map<string, PrefillWrite>;

interface UseRelatedCardPrefillProps {
  component: API.RelatedCardComponent;
  fields: FieldRecord;
  updateField: UpdateField;
  workflowTemplateId?: string | null;
  /** Shared across the whole form — see {@link PrefillWrites}. */
  prefillWrites: PrefillWrites;
}

/**
 * Fills a related card field from a card selected in another related card field, as a default:
 * the value of `lookup.lookupComponentId` on the followed field's first card is merged into this
 * field, deduped alongside anything already there.
 *
 * It fills from what the *form* put in the followed field, never from what the person using the
 * form put there. Two things count as the form filling it:
 *
 * - the value the followed field opens with, from a static default or a query param;
 * - a later value another prefill wrote into it, so a chain runs end to end — a Meter arriving as
 *   a query param fills Asset, and Asset being filled fills Location from it.
 *
 * Anything else is the person's own choice: picking or changing a card by hand leaves the fields
 * downstream of it exactly as they are, and touching a field a prefill filled hands it over to
 * them for good — putting the same card back does not restart the chain. `prefillWrites` is how
 * the two are told apart.
 *
 * It only ever *adds* cards and never removes or replaces existing items, so a manual edit is
 * never clobbered and clearing the followed field never clears this one.
 *
 * Runs independently of whether the field is visible on the form, so a field hidden by the view
 * template is still filled.
 */
export function useRelatedCardPrefill({
  component,
  fields,
  updateField,
  workflowTemplateId,
  prefillWrites,
}: UseRelatedCardPrefillProps): void {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId ?? undefined);

  const lookup = getRelatedCardDefaultLookup(component);

  const followedComponent = useMemo(
    () => getPrefillSourceComponent(component, workflowTemplate?.components),
    [component, workflowTemplate?.components],
  );

  const currentField = fields[component.id];
  const targetField = currentField instanceof RelatedEntityField ? currentField : null;

  const followedValue = !isNil(followedComponent) ? fields[followedComponent.id] : null;
  const followedField = followedValue instanceof RelatedEntityField ? followedValue : null;

  // Only the first card is followed — the feature is 1:1 by design, and concatenating a field
  // from every selected card is not what "prefill from the Asset" means.
  const firstFollowedId = first(followedField?.items ?? [])?.id ?? null;

  const {
    workflowEntities,
    loading,
    workflowTemplate: followedWorkflowTemplate,
  } = useWorkflowEntitiesByIds({
    workflowTemplateId: followedComponent?.workflowTemplateId,
    workflowEntityIds: isNil(firstFollowedId) ? [] : [firstFollowedId],
  });

  // The configured source field must still exist on the followed workflow. If the builder
  // archived or deleted it, prefill stops rather than falling back to a guess.
  const sourceComponentId = useMemo(() => {
    if (isNil(lookup)) {
      return undefined;
    }
    const exists = followedWorkflowTemplate?.components?.some(
      c => c.id === lookup.lookupComponentId,
    );
    return exists ? lookup.lookupComponentId : undefined;
  }, [lookup, followedWorkflowTemplate?.components]);

  const prefillItems = useMemo<RelatedItem[]>(() => {
    if (isNil(firstFollowedId) || isNil(sourceComponentId)) {
      return [];
    }
    const sourceEntity = workflowEntities.find(entity => entity.id === firstFollowedId);
    const rawValue = sourceEntity?.fields?.[sourceComponentId];
    return RelatedEntityField.fromFieldJson(rawValue)?.items ?? [];
  }, [firstFollowedId, sourceComponentId, workflowEntities]);

  // `undefined` sentinel means we haven't observed a followed id yet (distinct from `null`,
  // meaning the followed field is known to be empty).
  const previousFollowedIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (isNil(followedComponent)) {
      return;
    }
    // Wait for the followed card's data before deciding anything for this id.
    if (!isNil(firstFollowedId) && loading) {
      return;
    }

    // The first id observed is whatever the form opened with, so it needs no authorship check:
    // this hook mounts only once the create form has seeded its defaults and query-param
    // overrides. Every id after that is a change, and only counts if a prefill made it.
    const isFirstRun = previousFollowedIdRef.current === undefined;
    const hasChanged = firstFollowedId !== previousFollowedIdRef.current;
    previousFollowedIdRef.current = firstFollowedId;

    if (isNil(firstFollowedId) || !hasChanged) {
      return;
    }
    if (!isFirstRun && !prefillWrites.get(followedComponent.id)?.addedIds.has(firstFollowedId)) {
      return;
    }
    if (isEmpty(prefillItems)) {
      return;
    }

    const currentItems = targetField?.items ?? [];
    const currentIds = new Set(currentItems.map(item => item.id));
    const newItems = prefillItems.filter(item => !currentIds.has(item.id));
    if (isEmpty(newItems)) {
      return;
    }

    const resultItems = [...currentItems, ...newItems];
    // Recorded before the write, so the field following this one sees the authorship mark in the
    // same render that brings it the new value.
    prefillWrites.set(component.id, {
      addedIds: new Set(newItems.map(item => item.id)),
      resultIds: resultItems.map(item => item.id),
      hasLanded: false,
    });

    updateField(component.id, new RelatedEntityField(resultItems));
  }, [
    followedComponent,
    firstFollowedId,
    loading,
    prefillItems,
    prefillWrites,
    targetField,
    component.id,
    updateField,
  ]);

  // Hands the field over to the person the moment they change what a prefill left there. Dropping
  // the record is what stops them clearing this field, picking the same card again and having the
  // fields below it refill behind them — their pick is theirs, whatever it happens to be.
  useEffect(() => {
    const write = prefillWrites.get(component.id);
    if (isNil(write)) {
      return;
    }
    const currentIds = (targetField?.items ?? []).map(item => item.id);
    if (isEqual(currentIds, write.resultIds)) {
      write.hasLanded = true;
      return;
    }
    if (write.hasLanded) {
      prefillWrites.delete(component.id);
    }
  }, [prefillWrites, component.id, targetField]);
}
